import type { SurfaceLayerId } from '@/terrain/TerrainSurface';
import type { EnvironmentState } from '@/environment/EnvironmentState';
import { dateFromDayOfYear } from '@/environment/SeasonModel';
import { cellAt, describeGrid, type GridSpec } from '@/site/SiteGrid';
import type { SiteObjectKind } from '@/site/SiteObject';

/**
 * What the wand is told about the site before it is asked for anything.
 *
 * ## The rule this module exists to enforce
 *
 * The wand can only act on what it can name, and it can only name what is in here. So this record is
 * simultaneously the prompt's evidence *and* the UI's list of context chips — the panel renders the
 * same object the prompt serializes. That is the whole reason it is a plain record built by a pure
 * function rather than assembled inline while writing the prompt string: if the two could drift, the
 * chips would become a decorative claim about what was sent rather than a statement of it.
 *
 * ## What is deliberately not in here
 *
 * Not the whole site. A mature project has hundreds of plants, and listing them all would spend the
 * context window on a thousand near-identical rows to answer a question about one corner. Instead:
 * everything within a radius of the click is listed **individually with its id**, because those are
 * the plants a local request can act on; everything beyond it is summarised by species and count,
 * because that is all "what else is here" needs to mean. The same asymmetry the scene manifest
 * settled on for the capture prompt, applied to world space instead of screen space.
 *
 * Pure — no Three.js, no DOM, no clock. Every input is a value, so this is fully testable and the
 * text it produces is exactly reproducible from a saved project.
 */

/** How far around the click point plants are listed one by one rather than summarised. */
export const WAND_DETAIL_RADIUS_METERS = 12;

/** Cap on individually listed plants, so a dense bed cannot swamp the prompt. */
export const WAND_MAX_DETAILED_PLANTS = 40;

export interface WandPoint {
  x: number;
  z: number;
}

export interface WandPlantSummary {
  id: string;
  speciesId: string;
  /** Common name, for the model and for the chip label. */
  name: string;
  x: number;
  z: number;
  /** Metres from the click point. */
  distanceMeters: number;
  maturity: number;
  heightMeters: number;
  spreadMeters: number;
  selected: boolean;
}

export interface WandStructureSummary {
  id: string;
  name: string;
  /**
   * Widened from building/paving when props arrived: the wand describes what is in the scene, and a
   * bench the user can see and ask about has to be nameable. The reference-photo plan schema stays
   * narrowed, because that pipeline still only reconstructs built form from a photograph.
   */
  kind: SiteObjectKind;
  x: number;
  z: number;
  widthMeters: number;
  depthMeters: number;
  heightMeters: number;
  distanceMeters: number;
}

/**
 * An outline the designer drew on the ground.
 *
 * The reason this is a record of its own rather than a wider radius on the point: the *shape* is
 * the request. Someone who drags a kidney around the corner of a lawn has said something a centre
 * and a radius cannot say back, and the two consumers of this — the prompt, and the ops the model
 * writes into that shape — both need the vertices, not a summary of them.
 */
export interface WandRegion {
  /** The outline in world metres, `[x, z]`, closed implicitly. Counter-clockwise, deduplicated. */
  polygon: [number, number][];
  areaMeters: number;
  /** Axis-aligned extent, so scale can be read without doing polygon arithmetic. */
  widthMeters: number;
  lengthMeters: number;
  /** Centroid to the furthest vertex — how far from the middle the request reaches. */
  radiusMeters: number;
}

/**
 * What the user aimed at. The single most informative thing the wand is given.
 *
 * `area` is what a drag produces; the other three are what a click produces.
 */
export type WandTargetKind = 'ground' | 'plant' | 'structure' | 'area';

export interface WandTarget {
  kind: WandTargetKind;
  /**
   * Where on the ground the request is anchored, in world metres. For a drawn area this is the
   * outline's centroid — so "here" still resolves to a single point for anything that needs one.
   */
  point: WandPoint;
  groundHeightMeters: number;
  /** The ground material at the anchor point. */
  surface: SurfaceLayerId;
  /** Set when the click landed on a plant. */
  plant?: WandPlantSummary;
  /** Set when the click landed on a building or paving. */
  structure?: WandStructureSummary;
  /** Set when the designer drew an outline instead of clicking a point. */
  region?: WandRegion;
  /** Reference-grid cell the anchor fell in — set only while the grid overlay is on. */
  gridCell?: string;
}

export interface WandSiteSummary {
  widthMeters: number;
  lengthMeters: number;
  plantCount: number;
  speciesCount: number;
  /** Fraction of the site under each ground material, `0..1`. */
  coverage: Record<SurfaceLayerId, number>;
  /** Metres between the lowest and highest ground on the site. */
  reliefMeters: number;
}

export interface WandContext {
  site: WandSiteSummary;
  target: WandTarget;
  environment: EnvironmentState;
  /**
   * The reference grid, when the designer has it switched on.
   *
   * Present only while the overlay is visible, and that is deliberate rather than incidental: the
   * grid is in here because the *designer can see the labels*, so "plant a birch in C4" is a sentence
   * they might type. Sending the frame while nothing is drawn would invite the model to answer in
   * cell names the user has no way to check.
   */
  grid?: GridSpec;
  /** Plants inside {@link WAND_DETAIL_RADIUS_METERS}, nearest first, each with its id. */
  nearby: WandPlantSummary[];
  /** The user's selection, whether or not it is nearby. */
  selection: WandPlantSummary[];
  /** Species elsewhere on the site, as `speciesId → count`. Context, not a handle. */
  elsewhere: { speciesId: string; name: string; count: number }[];
  structures: WandStructureSummary[];
}

export interface BuildWandContextInput {
  site: Omit<WandSiteSummary, 'plantCount' | 'speciesCount'>;
  target: Omit<WandTarget, 'plant'> & { plantId?: string };
  environment: EnvironmentState;
  /** Every plant on the site. Filtering and summarising happen here, not at the call site. */
  plants: readonly WandPlantSummary[];
  structures: readonly WandStructureSummary[];
  selectedIds: readonly string[];
  detailRadiusMeters?: number;
  /** The reference grid, when it is switched on. Omitted when the overlay is hidden. */
  grid?: GridSpec;
}

/**
 * Assemble the context. The caller supplies raw per-plant facts; this decides what is near, what is
 * summarised, and what the target actually is.
 */
export function buildWandContext(input: BuildWandContextInput): WandContext {
  const region = input.target.region;
  // A drawn area is measured from its centroid, so the detail radius has to reach its far edge or a
  // request about a 30 m sweep would list only the plants nearest its middle.
  const radius = (input.detailRadiusMeters ?? WAND_DETAIL_RADIUS_METERS) + (region?.radiusMeters ?? 0);
  const selected = new Set(input.selectedIds);
  const point = input.target.point;

  const plants = input.plants.map((plant) => ({
    ...plant,
    distanceMeters: round(Math.hypot(plant.x - point.x, plant.z - point.z), 2),
    selected: selected.has(plant.id),
  }));

  const nearby = plants
    .filter((plant) => plant.distanceMeters <= radius)
    .sort((a, b) => a.distanceMeters - b.distanceMeters)
    .slice(0, WAND_MAX_DETAILED_PLANTS);

  const named = new Set(nearby.map((plant) => plant.id));
  const selection = plants.filter((plant) => plant.selected);
  for (const plant of selection) named.add(plant.id);

  const counts = new Map<string, { name: string; count: number }>();
  for (const plant of plants) {
    if (named.has(plant.id)) continue;
    const entry = counts.get(plant.speciesId) ?? { name: plant.name, count: 0 };
    entry.count += 1;
    counts.set(plant.speciesId, entry);
  }

  const target: WandTarget = {
    kind: input.target.kind,
    point: { x: round(point.x, 2), z: round(point.z, 2) },
    groundHeightMeters: round(input.target.groundHeightMeters, 2),
    surface: input.target.surface,
    ...(input.target.structure ? { structure: input.target.structure } : {}),
    ...(region ? { region } : {}),
    ...(input.grid ? { gridCell: cellAt(input.grid, point.x, point.z).label } : {}),
  };
  const hit = input.target.plantId
    ? plants.find((plant) => plant.id === input.target.plantId)
    : undefined;
  if (hit) target.plant = hit;

  return {
    site: {
      ...input.site,
      plantCount: plants.length,
      speciesCount: new Set(plants.map((plant) => plant.speciesId)).size,
    },
    target,
    environment: input.environment,
    ...(input.grid ? { grid: input.grid } : {}),
    nearby,
    selection,
    elsewhere: [...counts.entries()]
      .map(([speciesId, entry]) => ({ speciesId, name: entry.name, count: entry.count }))
      .sort((a, b) => b.count - a.count),
    structures: [...input.structures].sort((a, b) => a.distanceMeters - b.distanceMeters),
  };
}

/* -------------------------------- Rendering -------------------------------- */

/**
 * The context as the text the model actually receives.
 *
 * Written as a compact fact sheet rather than prose. A model reading this has to be able to copy an
 * id or a coordinate out of it without ambiguity, and every line it cannot use costs attention that
 * the ones it can use needed.
 */
export function describeWandContext(context: WandContext): string {
  const { site, target, environment } = context;
  const lines: string[] = [];

  lines.push(
    `# The site\n` +
      `${fmt(site.widthMeters)} x ${fmt(site.lengthMeters)} m, origin at the centre. ` +
      `x runs -${fmt(site.widthMeters / 2)}..${fmt(site.widthMeters / 2)}, ` +
      `z runs -${fmt(site.lengthMeters / 2)}..${fmt(site.lengthMeters / 2)}. ` +
      `Relief ${fmt(site.reliefMeters)} m. ` +
      `${site.plantCount} plants of ${site.speciesCount} species.\n` +
      `Ground cover: ${describeCoverage(site.coverage)}.`,
  );

  // Before the click, because it is the frame the click is then reported in.
  if (context.grid) lines.push(`# The reference grid\n${describeGrid(context.grid)}`);

  lines.push(
    target.region
      ? `# The area the user drew\n${describeTarget(target)}`
      : `# Where the user clicked\n${describeTarget(target)}`,
  );

  if (context.selection.length > 0) {
    lines.push(
      `# The user's selection (${context.selection.length})\n` +
        `Ops may address these with "target": { "selected": true }.\n` +
        context.selection.map(plantLine).join('\n'),
    );
  }

  if (context.nearby.length > 0) {
    lines.push(
      `# Plants near the click, nearest first\n` +
        `Address any of these by id.\n` +
        context.nearby.map(plantLine).join('\n'),
    );
  } else {
    lines.push('# Plants near the click\nNone — this is open ground.');
  }

  if (context.structures.length > 0) {
    lines.push(
      `# Buildings and paving\n` +
        context.structures
          .map(
            (structure) =>
              `  ${structure.id} — ${structure.name} (${structure.kind}), ` +
              `${fmt(structure.widthMeters)} x ${fmt(structure.depthMeters)} m, ` +
              `${fmt(structure.heightMeters)} m tall, centre (${fmt(structure.x)}, ${fmt(structure.z)}), ` +
              `${fmt(structure.distanceMeters)} m from the click`,
          )
          .join('\n'),
    );
  }

  if (context.elsewhere.length > 0) {
    lines.push(
      `# Elsewhere on the site\n` +
        `Counts only — these are not individually addressable.\n` +
        context.elsewhere
          .map((entry) => `  ${entry.speciesId} (${entry.name}) x${entry.count}`)
          .join('\n'),
    );
  }

  const date = dateFromDayOfYear(environment.date.dayOfYear);
  lines.push(
    `# Current environment\n` +
      `${pad(environment.time.hour)}:${pad(environment.time.minute)}, ` +
      `day ${environment.date.dayOfYear} (${MONTHS[date.month - 1]} ${date.day}), ` +
      `${environment.date.mode} mode, season ${environment.date.season}, ` +
      `weather ${environment.weather}, latitude ${fmt(environment.latitudeDegrees)}°.`,
  );

  return lines.join('\n\n');
}

function describeTarget(target: WandTarget): string {
  const where =
    `World point (${fmt(target.point.x)}, ${fmt(target.point.z)}), ` +
    (target.gridCell ? `grid cell ${target.gridCell}, ` : '') +
    `ground height ${fmt(target.groundHeightMeters)} m, surface ${target.surface}.`;

  // Before the plant and structure branches: a drag that happens to start over a shrub is still a
  // request about the shape, and reading it as a request about the shrub would ignore the gesture.
  const region = target.region;
  if (region) {
    return (
      `The designer DREW AN OUTLINE on the ground rather than clicking a point.\n` +
      `Outline, world metres, closed (the last vertex joins the first):\n` +
      `${JSON.stringify(region.polygon)}\n` +
      `Area ${fmt(region.areaMeters)} m². Bounding box ${fmt(region.widthMeters)} x ` +
      `${fmt(region.lengthMeters)} m. Centroid ${where}\n` +
      'THE SHAPE IS THE REQUEST. "Here", "this", "this area" and "it" all mean the inside of that ' +
      'outline — not a circle around its centre, and not the whole site.\n' +
      'Copy the outline into the ops that should honour it: a planting mass takes it as "polygon", ' +
      'a sculpt takes it as an "areas" entry, a paint region and a paving structure each take it ' +
      'as their own polygon. That is exact and costs you no arithmetic; approximating it with ' +
      'rectangles or a ring of drifts throws away the shape the designer drew by hand.\n' +
      'Size the work to the area given above — a 6 m² outline is a bed, a 200 m² one is a border.'
    );
  }

  if (target.plant) {
    return (
      `${where}\nThe click landed ON A PLANT: ${plantLine(target.plant).trim()}\n` +
      'Unless the request clearly concerns the wider area, it is about this plant.'
    );
  }
  if (target.structure) {
    const s = target.structure;
    return (
      `${where}\nThe click landed ON ${s.kind === 'building' ? 'A BUILDING' : 'PAVING'}: ` +
      `${s.id} — ${s.name}, ${fmt(s.widthMeters)} x ${fmt(s.depthMeters)} m.\n` +
      'A request about "it" means this object; a request about planting means the ground around it.'
    );
  }
  return `${where}\nThe click landed on OPEN GROUND. "Here" means this point.`;
}

function plantLine(plant: WandPlantSummary): string {
  return (
    `  ${plant.id} — ${plant.speciesId} (${plant.name}) at (${fmt(plant.x)}, ${fmt(plant.z)}), ` +
    `${fmt(plant.distanceMeters)} m away, maturity ${plant.maturity.toFixed(2)}, ` +
    `${fmt(plant.heightMeters)} m tall x ${fmt(plant.spreadMeters)} m wide` +
    (plant.selected ? ' [SELECTED]' : '')
  );
}

function describeCoverage(coverage: Record<SurfaceLayerId, number>): string {
  const parts = Object.entries(coverage)
    .filter(([, fraction]) => fraction >= 0.005)
    .sort((a, b) => b[1] - a[1])
    .map(([layer, fraction]) => `${layer} ${Math.round(fraction * 100)}%`);
  return parts.length > 0 ? parts.join(', ') : 'grass 100%';
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function fmt(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function round(value: number, places: number): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}
